/** What the client reads of a server error to recognise a rejected session. */
export type SessionRejectionCandidate = Readonly<{
  message?: string;
  extensions?: Readonly<Record<string, unknown>>;
}>;

function hasText(value: unknown): boolean {
  return typeof value === "string" && value.length > 0;
}

/** The backend said what kind of failure this is; its message decides nothing. */
function isClassified(error: SessionRejectionCandidate): boolean {
  return hasText(error.extensions?.errorClass);
}

/**
 * Whether a server error is the rejection of a missing or expired session,
 * which the login redirect handles and nobody has to read: the transport says
 * so (`UNAUTHENTICATED`), or the error has the class `auth` and no code of its
 * own. An `auth` error with a code (email not verified, sign-up required) is
 * shown. The message text "Unauthorized" only decides for an error the
 * backend did not classify.
 */
export function isSessionRejection(error: SessionRejectionCandidate): boolean {
  const extensions = error.extensions;
  if (
    extensions?.code === "UNAUTHENTICATED" ||
    (extensions?.errorClass === "auth" && !hasText(extensions.errorCode))
  ) {
    return true;
  }
  return !isClassified(error) && (error.message ?? "").includes("Unauthorized");
}
